/**
 * Human labels for a bet row.
 *
 * Since 0051 GET /bets carries durable `marketLabel`, `selectionLabel`,
 * `eventLabel` and `sport` captured at placement (per leg on a multi). The
 * board the customer is looking at is still preferred when it holds the market
 * (it is the freshest copy), then what the API sent, and only then a short,
 * stable form of the ids so a pre-0051 row never reads as a bare hash.
 */
import type { Market } from '@/types/api';

export interface BetLabels {
  eventLabel: string;
  marketLabel: string;
  selectionLabel: string;
  sport: string | null;
  /** True when every label came from a real source rather than a shortened id. */
  resolved: boolean;
}

function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 6)}…${id.slice(-3)}` : id;
}

/** A single ticket's top-level ids, or one leg of a multi — both carry these four fields. */
export interface LabelSource {
  marketId: string;
  selectionId: string;
  marketLabel?: string;
  selectionLabel?: string;
  /** Fixture label / sport captured at placement (0051 durable labels); null for a multi parent. */
  eventLabel?: string | null;
  sport?: string | null;
  /** Race number for racing fixtures (0053); null/undefined for team sports. */
  raceNumber?: number | null;
}

export function labelsForBet(bet: LabelSource, markets: Market[] | undefined): BetLabels {
  const market = markets?.find((m) => m.id === bet.marketId);
  const selection = market?.selections.find((s) => s.id === bet.selectionId);
  const selectionLabel = selection?.label || bet.selectionLabel || '';
  const marketLabel = market?.marketLabel || bet.marketLabel || '';
  const baseEvent = market?.eventLabel || bet.eventLabel || '';
  const raceNumber = (market as { raceNumber?: number | null } | undefined)?.raceNumber ?? bet.raceNumber ?? null;
  // "R7 · Lakeside (AUS) 17th Oct" — the race number leads so a punter scanning
  // My bets finds the race, not the meeting (stakeholder 2026-10-18, item 4).
  const eventLabel = baseEvent && raceNumber ? `R${raceNumber} · ${baseEvent}` : baseEvent;
  return {
    eventLabel: eventLabel || (marketLabel ? '' : `Market ${shortId(bet.marketId)}`),
    marketLabel,
    selectionLabel: selectionLabel || `Selection ${shortId(bet.selectionId)}`,
    sport: market?.sport ?? bet.sport ?? null,
    resolved: Boolean(selectionLabel && (eventLabel || marketLabel)),
  };
}
